// Web Push client: service worker registration + Push API subscription.
// Mirrors the Atelier pattern, adapted to Launchpad security (CSRF header on mutations).

export type PushState = "on" | "off" | "denied" | "unsupported";

// Bump the version when public/sw.js changes: a new URL always busts edge caches.
export const SW_URL = "/sw.js?v=1";

// iOS Safari supports web push only from an installed (standalone) PWA.
export function isIos(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches
    || (navigator as { standalone?: boolean }).standalone === true;
}

export function needsIosInstallForPush(): boolean {
  return isIos() && !isStandalone() && !supported();
}

function supported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function waitForActive(registration: ServiceWorkerRegistration): Promise<void> {
  if (registration.active) return Promise.resolve();
  const worker = registration.installing ?? registration.waiting;
  if (!worker) return Promise.resolve();
  return new Promise((resolve) => {
    const check = () => {
      if (worker.state === "activated" || worker.state === "redundant") {
        worker.removeEventListener("statechange", check);
        resolve();
      }
    };
    worker.addEventListener("statechange", check);
    check();
  });
}

export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.register(SW_URL, { scope: "/" }).catch(() => {
    /* registration is retried on the next load */
  });
}

/** Push endpoint of THIS device; identifies the subscription for per-device settings. */
export async function getPushEndpoint(): Promise<string | null> {
  try {
    const registration = await navigator.serviceWorker.getRegistration("/");
    const subscription = registration ? await registration.pushManager.getSubscription() : null;
    return subscription?.endpoint ?? null;
  } catch {
    return null;
  }
}

export async function pushState(): Promise<PushState> {
  if (!supported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  try {
    const registration = await navigator.serviceWorker.getRegistration("/");
    if (!registration) return "off";
    registration.update().catch(() => {});
    const subscription = await registration.pushManager.getSubscription();
    return subscription ? "on" : "off";
  } catch {
    return "off";
  }
}

export async function enablePush(csrfToken: string): Promise<PushState> {
  if (!supported()) return "unsupported";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "off";
  const config = await fetch("/api/notifications/push-config", { credentials: "same-origin" })
    .then((response) => response.json() as Promise<{ publicKey?: string | null }>)
    .catch(() => null);
  if (!config?.publicKey) throw new Error("push_not_configured");
  const registration = await navigator.serviceWorker.register(SW_URL, { scope: "/" });
  await waitForActive(registration);
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.publicKey) as BufferSource
    });
  }
  const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  const response = await fetch("/api/notifications/subscriptions", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": csrfToken },
    credentials: "same-origin",
    body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys })
  });
  if (!response.ok) throw new Error("subscription_save_failed");
  return pushState();
}

export async function disablePush(csrfToken: string): Promise<PushState> {
  if (!supported()) return "unsupported";
  try {
    const registration = await navigator.serviceWorker.getRegistration("/");
    const subscription = registration ? await registration.pushManager.getSubscription() : null;
    if (subscription) {
      await fetch("/api/notifications/subscriptions/disable", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": csrfToken },
        credentials: "same-origin",
        body: JSON.stringify({ endpoint: subscription.endpoint })
      }).catch(() => {});
      await subscription.unsubscribe();
    }
  } catch {
    /* already unsubscribed */
  }
  return pushState();
}
